import { useNavigate } from 'react-router-dom'
import HmacSHA1 from 'crypto-js/hmac-sha1'
import queryString from 'query-string'
import { fitonGetProfilePromise } from 'apis/FitOnAuthAPI'
import { getFitonToken } from 'utils/authLocalStorage'
import { useFlow } from './useFlow'
import { useMCA } from 'views/Care/MCA/useMCA'
import { ScreenKey } from 'views/Care/util'
import _ from 'lodash'

// returnTo (e.g. McaAssessment.js's own URL) can carry a fitonUserId query
// param for a different account than whoever actually just logged in here -
// the link may have been generated/shared before this browser switched
// accounts. The account that just authenticated is the one continuing the
// flow, so returnTo must point at that account, not the stale one.
const withLoggedInFitonUserId = (returnTo, fitonUserId) => {
  if (!returnTo) return returnTo
  const [path, search] = returnTo.split('?')
  const params = queryString.parse(search || '')
  if (!params.fitonUserId || params.fitonUserId === fitonUserId.toString()) {
    return returnTo
  }
  return `${path}?${queryString.stringify({ ...params, fitonUserId })}`
}

// Extracted out of useFitonAuth so entry points that need to resume an
// existing FitOn session don't have to pull in signup/login form state just
// to reach this logic (Welcome.js's MCA "Get Started", and /login, /signup,
// /about-you when opened directly via URL — see tryAutoLoginFromStoredSession
// below).
export const useMcaEnrollment = () => {
  const navigate = useNavigate()
  const { handleUserAutoLogin: userCareAutoLogin } = useFlow({
    openInitialScreen: false,
  })
  const { getMcaEnrollment, getCurrentEnrollment } = useMCA({ sku: 'mca' })

  const navigateToAboutYou = ({
    fitonUserId,
    fitonEmail,
    variant,
    gender,
    t,
    h,
  }) => {
    navigate('/about-you', {
      state: {
        fitonUserId,
        fitonEmail,
        variant,
        gender,
        t,
        h,
      },
    })
  }

  const handleUserAutoLogin = async ({
    fitonUserId,
    fitonEmail,
    variant,
    gender,
    // Set only by callers resuming a direct visit to a private route (e.g.
    // McaAssessment.js) that got bounced through /login for lack of a
    // session. Only honored when the user turns out to already be enrolled —
    // a not-yet-enrolled user still needs About You/the screener regardless
    // of where they were trying to go.
    returnTo,
  }) => {
    const t = Math.floor(Date.now() / 1000).toString()
    const h = HmacSHA1(
      `${fitonUserId.toString()}${fitonEmail}${t}`,
      process.env.REACT_APP_autoLoginSecret
    ).toString()
    const user = await userCareAutoLogin({
      fitonUserId,
      email: fitonEmail,
      t: t,
      h: h,
    })
    if (user?.data && user.code === 200) {
      const correctedReturnTo = withLoggedInFitonUserId(returnTo, fitonUserId)
      const { currentKey, enrollmentResp: enrollment } =
        await getCurrentEnrollment()
      if (currentKey === ScreenKey.MCA_COMING_SOON_STATE) {
        navigate('/?variant=mca', { state: { currentKey } })
        return
      }
      if (_.isEmpty(enrollment) && correctedReturnTo) {
        navigate(correctedReturnTo, { replace: true })
        return
      }
      if (enrollment?.alreadyEnrolledInCareSku) {
        if (correctedReturnTo) {
          navigate(correctedReturnTo, { replace: true })
          return
        }
        navigate('/?variant=mca', {
          state: { currentKey: ScreenKey.MCA_ALREADY_ENROLLED },
        })
        return
      }
      if (!_.isEmpty(enrollment)) {
        if (currentKey === ScreenKey.MCA_ABOUT_YOU) {
          navigateToAboutYou({ fitonUserId, fitonEmail, variant, gender, t, h })
        } else {
          navigate('/?variant=mca', { state: { currentKey } })
        }
        return
      }
    }

    navigateToAboutYou({ fitonUserId, fitonEmail, variant, gender, t, h })
  }

  // Resumes a FitOn session already on this device (a stored FitOn token from
  // a prior native signup/login) instead of asking for credentials again —
  // Welcome.js's MCA "Get Started", and /login, /signup, /about-you when
  // opened directly via URL. Fetches the profile fresh off the stored token
  // (rather than trusting any locally cached id/email, which this app
  // doesn't keep anyway) and then runs the same handleUserAutoLogin flow a
  // fresh signup/login would. Returns false — doing nothing — when there's
  // no token or the profile fetch comes back empty, so the caller can fall
  // back to its normal signup/login flow.
  const tryAutoLoginFromStoredSession = async ({
    variant,
    gender,
    returnTo,
  } = {}) => {
    if (!getFitonToken()) return false
    const resp = await fitonGetProfilePromise()
    const user = resp?.data
    if (!user?.id) return false
    await handleUserAutoLogin({
      fitonUserId: user.id,
      fitonEmail: user.email,
      variant,
      gender,
      returnTo,
    })
    return true
  }

  return {
    handleUserAutoLogin,
    tryAutoLoginFromStoredSession,
    getMcaEnrollment,
  }
}
